import { useEffect, useState } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Footer } from '../components/Footer';
import { ClinicalChartCard } from '../components/ClinicalChartCard';
import { RefractionGrid } from '../components/RefractionGrid';
import {
  createClinicalVisit,
  updateClinicalVisit,
  ClinicalVisitRequest,
  getClinicalChart,
  ClinicalChartData,
} from '../services/api';
import { PrimaryButton } from '../components/PrimaryButton';

interface RetinoscopiaForm {
  od_static_val: string;
  od_static_av: string;
  od_dynamic_val: string;
  od_dynamic_av: string;
  oi_static_val: string;
  oi_static_av: string;
  oi_dynamic_val: string;
  oi_dynamic_av: string;
}

interface VisualAcuityForm {
  od_distance_sc: string;
  od_distance_cc: string;
  od_ph: string;
  oi_distance_sc: string;
  oi_distance_cc: string;
  oi_ph: string;
  dip_mm: string;
  dnp_od_mm: string;
  dnp_oi_mm: string;
  od_height_mm: string;
  oi_height_mm: string;
}

interface RefractionForm {
  od_sphere_far: string;
  od_cylinder_far: string;
  od_axis_far: string;
  od_add_far: string;
  od_prisma_far: string;
  od_sphere_near: string;
  od_cylinder_near: string;
  od_axis_near: string;
  od_add_near: string;
  od_prisma_near: string;
  oi_sphere_far: string;
  oi_cylinder_far: string;
  oi_axis_far: string;
  oi_add_far: string;
  oi_prisma_far: string;
  oi_sphere_near: string;
  oi_cylinder_near: string;
  oi_axis_near: string;
  oi_add_near: string;
  oi_prisma_near: string;
}

interface LocationState {
  editMode?: boolean;
}

export const ClinicalChart = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { patient_id, visit_id } = useParams<{ patient_id: string; visit_id?: string }>();
  const { editMode: initialEditMode } = (location.state as LocationState) || {};
  const [isEditing, setIsEditing] = useState(initialEditMode || false);
  const isViewMode = Boolean(visit_id) && !isEditing;
  
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingChart, setIsFetchingChart] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [retinoscopia, setRetinoscopia] = useState<RetinoscopiaForm>({
    od_static_val: '',
    od_static_av: '',
    od_dynamic_val: '',
    od_dynamic_av: '',
    oi_static_val: '',
    oi_static_av: '',
    oi_dynamic_val: '',
    oi_dynamic_av: '',
  });

  const [visualAcuity, setVisualAcuity] = useState<VisualAcuityForm>({
    od_distance_sc: '',
    od_distance_cc: '',
    od_ph: '',
    oi_distance_sc: '',
    oi_distance_cc: '',
    oi_ph: '',
    dip_mm: '',
    dnp_od_mm: '',
    dnp_oi_mm: '',
    od_height_mm: '',
    oi_height_mm: '',
  });

  // Lensometry - Current glasses for reference
  const [lensometry, setLensometry] = useState<RefractionForm>({
    od_sphere_far: '',
    od_cylinder_far: '',
    od_axis_far: '',
    od_add_far: '',
    od_prisma_far: '',
    od_sphere_near: '',
    od_cylinder_near: '',
    od_axis_near: '',
    od_add_near: '',
    od_prisma_near: '',
    oi_sphere_far: '',
    oi_cylinder_far: '',
    oi_axis_far: '',
    oi_add_far: '',
    oi_prisma_far: '',
    oi_sphere_near: '',
    oi_cylinder_near: '',
    oi_axis_near: '',
    oi_add_near: '',
    oi_prisma_near: '',
  });

  // Final RX - The primary prescription to print/save
  const [finalRx, setFinalRx] = useState<RefractionForm>({
    od_sphere_far: '',
    od_cylinder_far: '',
    od_axis_far: '',
    od_add_far: '',
    od_prisma_far: '',
    od_sphere_near: '',
    od_cylinder_near: '',
    od_axis_near: '',
    od_add_near: '',
    od_prisma_near: '',
    oi_sphere_far: '',
    oi_cylinder_far: '',
    oi_axis_far: '',
    oi_add_far: '',
    oi_prisma_far: '',
    oi_sphere_near: '',
    oi_cylinder_near: '',
    oi_axis_near: '',
    oi_add_near: '',
    oi_prisma_near: '',
  });

  const [diagnosis, setDiagnosis] = useState('');

  // Symptoms state
  const SYMPTOM_OPTIONS = ['Lagrimeo', 'Cefalea', 'Epifora', 'Diplopia', 'Fotofobia', 'Emborronamiento', 'Ardor', 'Prurito', 'Fatiga Visual'];
  const [selectedSymptoms, setSelectedSymptoms] = useState<string[]>([]);
  const [otherSymptom, setOtherSymptom] = useState('');

  // Helper to parse symptoms string from API
  const parseSymptomsFromApi = (symptomsStr: string) => {
    if (!symptomsStr) return;
    const parts = symptomsStr.split(',').map(s => s.trim()).filter(Boolean);
    const known: string[] = [];
    const other: string[] = [];
    parts.forEach(part => {
      if (SYMPTOM_OPTIONS.includes(part)) {
        known.push(part);
      } else {
        other.push(part);
      }
    });
    setSelectedSymptoms(known);
    setOtherSymptom(other.join(', '));
  };

  // Helper to build symptoms string for API
  const buildSymptomsForApi = (): string => {
    const all = [...selectedSymptoms];
    if (otherSymptom.trim()) {
      all.push(otherSymptom.trim());
    }
    return all.join(', ');
  };

  useEffect(() => {
    const loadChart = async () => {
      if (!visit_id) return;

      setIsFetchingChart(true);
      setError(null);

      try {
        const result = await getClinicalChart(visit_id);
        if (!result.data) {
          throw new Error('Ficha clínica no disponible');
        }

        const chart: ClinicalChartData = result.data;

        setRetinoscopia({
          od_static_val: chart.retinoscopy?.od_static_val || '',
          od_static_av: chart.retinoscopy?.od_static_av || '',
          od_dynamic_val: chart.retinoscopy?.od_dynamic_val || '',
          od_dynamic_av: chart.retinoscopy?.od_dynamic_av || '',
          oi_static_val: chart.retinoscopy?.oi_static_val || '',
          oi_static_av: chart.retinoscopy?.oi_static_av || '',
          oi_dynamic_val: chart.retinoscopy?.oi_dynamic_val || '',
          oi_dynamic_av: chart.retinoscopy?.oi_dynamic_av || '',
        });

        setVisualAcuity({
          od_distance_sc: chart.visual_acuity?.od_distance_sc || '',
          od_distance_cc: chart.visual_acuity?.od_distance_cc || '',
          od_ph: chart.visual_acuity?.od_ph || '',
          oi_distance_sc: chart.visual_acuity?.oi_distance_sc || '',
          oi_distance_cc: chart.visual_acuity?.oi_distance_cc || '',
          oi_ph: chart.visual_acuity?.oi_ph || '',
          dip_mm: chart.visual_acuity?.od_dip_mm?.toString() || '',
          dnp_od_mm: chart.visual_acuity?.od_dnp_mm?.toString() || '',
          dnp_oi_mm: chart.visual_acuity?.oi_dnp_mm?.toString() || '',
          od_height_mm: chart.visual_acuity?.od_height_mm?.toString() || '',
          oi_height_mm: chart.visual_acuity?.oi_height_mm?.toString() || '',
        });

        // Load lensometry (current glasses) for reference
        setLensometry({
          od_sphere_far: chart.lensometry?.od_sphere_far?.toString() || '',
          od_cylinder_far: chart.lensometry?.od_cylinder_far?.toString() || '',
          od_axis_far: chart.lensometry?.od_axis_far?.toString() || '',
          od_add_far: chart.lensometry?.od_add_far?.toString() || '',
          od_prisma_far: chart.lensometry?.od_prisma_far || '',
          od_sphere_near: chart.lensometry?.od_sphere_near?.toString() || '',
          od_cylinder_near: chart.lensometry?.od_cylinder_near?.toString() || '',
          od_axis_near: chart.lensometry?.od_axis_near?.toString() || '',
          od_add_near: chart.lensometry?.od_add_near?.toString() || '',
          od_prisma_near: chart.lensometry?.od_prisma_near || '',
          oi_sphere_far: chart.lensometry?.oi_sphere_far?.toString() || '',
          oi_cylinder_far: chart.lensometry?.oi_cylinder_far?.toString() || '',
          oi_axis_far: chart.lensometry?.oi_axis_far?.toString() || '',
          oi_add_far: chart.lensometry?.oi_add_far?.toString() || '',
          oi_prisma_far: chart.lensometry?.oi_prisma_far || '',
          oi_sphere_near: chart.lensometry?.oi_sphere_near?.toString() || '',
          oi_cylinder_near: chart.lensometry?.oi_cylinder_near?.toString() || '',
          oi_axis_near: chart.lensometry?.oi_axis_near?.toString() || '',
          oi_add_near: chart.lensometry?.oi_add_near?.toString() || '',
          oi_prisma_near: chart.lensometry?.oi_prisma_near || '',
        });

        // Load final RX (prescription to print)
        setFinalRx({
          od_sphere_far: chart.final_rx?.od_sphere_far?.toString() || '',
          od_cylinder_far: chart.final_rx?.od_cylinder_far?.toString() || '',
          od_axis_far: chart.final_rx?.od_axis_far?.toString() || '',
          od_add_far: chart.final_rx?.od_add_far?.toString() || '',
          od_prisma_far: chart.final_rx?.od_prisma_far || '',
          od_sphere_near: chart.final_rx?.od_sphere_near?.toString() || '',
          od_cylinder_near: chart.final_rx?.od_cylinder_near?.toString() || '',
          od_axis_near: chart.final_rx?.od_axis_near?.toString() || '',
          od_add_near: chart.final_rx?.od_add_near?.toString() || '',
          od_prisma_near: chart.final_rx?.od_prisma_near || '',
          oi_sphere_far: chart.final_rx?.oi_sphere_far?.toString() || '',
          oi_cylinder_far: chart.final_rx?.oi_cylinder_far?.toString() || '',
          oi_axis_far: chart.final_rx?.oi_axis_far?.toString() || '',
          oi_add_far: chart.final_rx?.oi_add_far?.toString() || '',
          oi_prisma_far: chart.final_rx?.oi_prisma_far || '',
          oi_sphere_near: chart.final_rx?.oi_sphere_near?.toString() || '',
          oi_cylinder_near: chart.final_rx?.oi_cylinder_near?.toString() || '',
          oi_axis_near: chart.final_rx?.oi_axis_near?.toString() || '',
          oi_add_near: chart.final_rx?.oi_add_near?.toString() || '',
          oi_prisma_near: chart.final_rx?.oi_prisma_near || '',
        });

        setDiagnosis(chart.visit?.diagnosis || '');
        parseSymptomsFromApi(chart.visit?.symptoms || '');
      } catch (err) {
        console.error('Error loading clinical chart:', err);
        setError(err instanceof Error ? err.message : 'No se pudo cargar la ficha clínica');
      } finally {
        setIsFetchingChart(false);
      }
    };

    loadChart();
  }, [visit_id]);

  const handleRetinoscopiaChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setRetinoscopia(prev => ({ ...prev, [name]: value }));
  };

  const handleVisualAcuityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setVisualAcuity(prev => ({ ...prev, [name]: value }));
  };

  const handleLensometryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setLensometry(prev => ({ ...prev, [name]: value }));
  };

  const handleFinalRxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFinalRx(prev => ({ ...prev, [name]: value }));
  };

  const validateFinalRx = (): boolean => {
    // Validate that at least one field in the final RX prescription is filled
    return Object.values(finalRx).some(value => value.trim() !== '');
  };

  const handleSubmit = async () => {
    if (isViewMode) return;

    if (!patient_id) {
      setError('ID de paciente no encontrado');
      return;
    }

    if (!validateFinalRx()) {
      setError('Debe completar al menos un campo en la Receta Final (RX Final)');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const finalRxData = {
        od_sphere_far: finalRx.od_sphere_far ? parseFloat(finalRx.od_sphere_far) : undefined,
        od_cylinder_far: finalRx.od_cylinder_far ? parseFloat(finalRx.od_cylinder_far) : undefined,
        od_axis_far: finalRx.od_axis_far ? parseInt(finalRx.od_axis_far) : undefined,
        od_add_far: finalRx.od_add_far ? parseFloat(finalRx.od_add_far) : undefined,
        od_prisma_far: finalRx.od_prisma_far || '',
        od_sphere_near: finalRx.od_sphere_near ? parseFloat(finalRx.od_sphere_near) : undefined,
        od_cylinder_near: finalRx.od_cylinder_near ? parseFloat(finalRx.od_cylinder_near) : undefined,
        od_axis_near: finalRx.od_axis_near ? parseInt(finalRx.od_axis_near) : undefined,
        od_add_near: finalRx.od_add_near ? parseFloat(finalRx.od_add_near) : undefined,
        od_prisma_near: finalRx.od_prisma_near || '',
        oi_sphere_far: finalRx.oi_sphere_far ? parseFloat(finalRx.oi_sphere_far) : undefined,
        oi_cylinder_far: finalRx.oi_cylinder_far ? parseFloat(finalRx.oi_cylinder_far) : undefined,
        oi_axis_far: finalRx.oi_axis_far ? parseInt(finalRx.oi_axis_far) : undefined,
        oi_add_far: finalRx.oi_add_far ? parseFloat(finalRx.oi_add_far) : undefined,
        oi_prisma_far: finalRx.oi_prisma_far || '',
        oi_sphere_near: finalRx.oi_sphere_near ? parseFloat(finalRx.oi_sphere_near) : undefined,
        oi_cylinder_near: finalRx.oi_cylinder_near ? parseFloat(finalRx.oi_cylinder_near) : undefined,
        oi_axis_near: finalRx.oi_axis_near ? parseInt(finalRx.oi_axis_near) : undefined,
        oi_add_near: finalRx.oi_add_near ? parseFloat(finalRx.oi_add_near) : undefined,
        oi_prisma_near: finalRx.oi_prisma_near || '',
      };

      const retinoscopiaData = {
        od_static_val: retinoscopia.od_static_val || undefined,
        od_static_av: retinoscopia.od_static_av || undefined,
        od_dynamic_val: retinoscopia.od_dynamic_val || undefined,
        od_dynamic_av: retinoscopia.od_dynamic_av || undefined,
        oi_static_val: retinoscopia.oi_static_val || undefined,
        oi_static_av: retinoscopia.oi_static_av || undefined,
        oi_dynamic_val: retinoscopia.oi_dynamic_val || undefined,
        oi_dynamic_av: retinoscopia.oi_dynamic_av || undefined,
      };

      const visualAcuityData = {
        od_distance_sc: visualAcuity.od_distance_sc || undefined,
        od_distance_cc: visualAcuity.od_distance_cc || undefined,
        od_ph: visualAcuity.od_ph || undefined,
        od_dip_mm: visualAcuity.dip_mm ? parseFloat(visualAcuity.dip_mm) : undefined,
        od_dnp_mm: visualAcuity.dnp_od_mm ? parseFloat(visualAcuity.dnp_od_mm) : undefined,
        od_height_mm: visualAcuity.od_height_mm ? parseFloat(visualAcuity.od_height_mm) : undefined,
        oi_distance_sc: visualAcuity.oi_distance_sc || undefined,
        oi_distance_cc: visualAcuity.oi_distance_cc || undefined,
        oi_ph: visualAcuity.oi_ph || undefined,
        oi_dip_mm: visualAcuity.dip_mm ? parseFloat(visualAcuity.dip_mm) : undefined,
        oi_dnp_mm: visualAcuity.dnp_oi_mm ? parseFloat(visualAcuity.dnp_oi_mm) : undefined,
        oi_height_mm: visualAcuity.oi_height_mm ? parseFloat(visualAcuity.oi_height_mm) : undefined,
      };

      // Build lensometry data
      const lensometryData = {
        od_sphere_far: lensometry.od_sphere_far ? parseFloat(lensometry.od_sphere_far) : undefined,
        od_cylinder_far: lensometry.od_cylinder_far ? parseFloat(lensometry.od_cylinder_far) : undefined,
        od_axis_far: lensometry.od_axis_far ? parseInt(lensometry.od_axis_far) : undefined,
        od_add_far: lensometry.od_add_far ? parseFloat(lensometry.od_add_far) : undefined,
        od_prisma_far: lensometry.od_prisma_far || '',
        od_sphere_near: lensometry.od_sphere_near ? parseFloat(lensometry.od_sphere_near) : undefined,
        od_cylinder_near: lensometry.od_cylinder_near ? parseFloat(lensometry.od_cylinder_near) : undefined,
        od_axis_near: lensometry.od_axis_near ? parseInt(lensometry.od_axis_near) : undefined,
        od_add_near: lensometry.od_add_near ? parseFloat(lensometry.od_add_near) : undefined,
        od_prisma_near: lensometry.od_prisma_near || '',
        oi_sphere_far: lensometry.oi_sphere_far ? parseFloat(lensometry.oi_sphere_far) : undefined,
        oi_cylinder_far: lensometry.oi_cylinder_far ? parseFloat(lensometry.oi_cylinder_far) : undefined,
        oi_axis_far: lensometry.oi_axis_far ? parseInt(lensometry.oi_axis_far) : undefined,
        oi_add_far: lensometry.oi_add_far ? parseFloat(lensometry.oi_add_far) : undefined,
        oi_prisma_far: lensometry.oi_prisma_far || '',
        oi_sphere_near: lensometry.oi_sphere_near ? parseFloat(lensometry.oi_sphere_near) : undefined,
        oi_cylinder_near: lensometry.oi_cylinder_near ? parseFloat(lensometry.oi_cylinder_near) : undefined,
        oi_axis_near: lensometry.oi_axis_near ? parseInt(lensometry.oi_axis_near) : undefined,
        oi_add_near: lensometry.oi_add_near ? parseFloat(lensometry.oi_add_near) : undefined,
        oi_prisma_near: lensometry.oi_prisma_near || '',
      };

      if (isEditing && visit_id) {
        // Update existing visit
        await updateClinicalVisit(visit_id, {
          diagnosis: diagnosis || undefined,
          symptoms: buildSymptomsForApi() || undefined,
          retinoscopy: retinoscopiaData,
          visual_acuity: visualAcuityData,
          final_rx: finalRxData,
          lensometry: lensometryData,
        });

        setIsEditing(false);
        // Redirect to patient history
        navigate(`/history/${patient_id}`);
      } else {
        // Create new visit
        const payload: ClinicalVisitRequest = {
          patient_id: parseInt(patient_id),
          diagnosis: diagnosis || undefined,
          symptoms: buildSymptomsForApi() || undefined,
          retinoscopy: retinoscopiaData,
          visual_acuity: visualAcuityData,
          final_rx: finalRxData,
          lensometry: lensometryData,
        };

        await createClinicalVisit(payload);

        // Redirect to patient history
        navigate(`/history/${patient_id}`);
      }
    } catch (err) {
      console.error('Error saving clinical visit:', err);
      setError(err instanceof Error ? err.message : 'Error al guardar la visita clínica');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    navigate('/home');
  };

  const handleViewHistory = () => {
    if (patient_id) {
      navigate(`/history/${patient_id}`);
    }
  };

  const inputClass = "w-full rounded-lg border border-slate-200 bg-[#f6f7f8] p-3 text-center text-lg text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-colors";
  const isSubmitDisabled = isLoading || isFetchingChart;
  const submitLabel = isFetchingChart
    ? 'Cargando ficha...'
    : isLoading
      ? 'Guardando...'
      : 'Finalizar y Prescribir';

  return (
    <div className="relative flex min-h-screen w-full flex-col bg-[#f6f7f8]">
      {/* Header */}
      <header className="sticky top-0 z-10 flex w-full flex-col items-center bg-[#f6f7f8]/80 backdrop-blur-sm">
        <div className="flex w-full max-w-6xl items-center justify-between gap-4 px-4 py-3 sm:px-8">
          <div className="flex flex-col">
            <div className="flex items-center gap-3">
              <h1 className="text-3xl font-black text-[#101822] leading-tight tracking-[-0.033em]">
                Ficha Clínica
              </h1>
              {visit_id && !isEditing && (
                <button
                  type="button"
                  onClick={() => setIsEditing(true)}
                  className="flex items-center justify-center w-10 h-10 rounded-full bg-slate-100 text-slate-600 hover:bg-primary hover:text-white transition-colors"
                  title="Editar ficha"
                >
                  <span className="material-symbols-outlined" style={{ fontVariationSettings: '"FILL" 0, "wght" 300, "GRAD" 0, "opsz" 24' }}>edit</span>
                </button>
              )}
              {isEditing && (
                <span className="inline-flex items-center gap-1 rounded-full border border-primary bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
                  <span className="material-symbols-outlined text-sm" style={{ fontVariationSettings: '"FILL" 0, "wght" 300, "GRAD" 0, "opsz" 20' }}>edit</span>
                  Editando
                </span>
              )}
            </div>
            <p className="flex items-center gap-2 text-base font-normal text-slate-500">
              <span>Paciente #{patient_id}</span>
              {isViewMode && (
                <span className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-slate-100 px-2 py-0.5 text-xs font-semibold text-slate-700">
                  <span className="material-symbols-outlined text-sm" style={{ fontVariationSettings: '"FILL" 0, "wght" 300, "GRAD" 0, "opsz" 20' }}>visibility</span>
                  Solo lectura
                </span>
              )}
            </p>
          </div>
        </div>
        <div className="h-px w-full bg-slate-200"></div>
      </header>

      {/* Main Content */}
      <main className="flex flex-1 justify-center py-8">
        <div className="w-full max-w-6xl px-4 sm:px-8">
          <div className="flex flex-col gap-6">
            {/* Error Alert */}
            {error && (
              <div className="rounded-xl bg-red-50 border border-red-200 p-4">
                <p className="text-red-800">{error}</p>
              </div>
            )}

            {isFetchingChart && (
              <div className="rounded-xl bg-white border border-slate-200 p-4 text-slate-600">
                Cargando ficha clínica...
              </div>
            )}

            {/* Symptoms Section */}
            <ClinicalChartCard title="Motivo de Consulta" collapsible>
              <div className="flex flex-col gap-6">
                <div className="flex flex-wrap gap-3">
                  {SYMPTOM_OPTIONS.map((symptom) => (
                    <label key={symptom} className={`flex h-10 cursor-pointer items-center justify-center gap-x-2 rounded-xl px-4 transition-colors ${selectedSymptoms.includes(symptom) ? 'bg-primary text-white' : 'bg-slate-100 text-[#101822]'} ${isViewMode ? 'cursor-default' : ''}`}>
                      <input 
                        className="hidden" 
                        type="checkbox"
                        checked={selectedSymptoms.includes(symptom)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedSymptoms(prev => [...prev, symptom]);
                          } else {
                            setSelectedSymptoms(prev => prev.filter(s => s !== symptom));
                          }
                        }}
                        disabled={isViewMode}
                      />
                      <span className="text-sm font-medium">{symptom}</span>
                    </label>
                  ))}
                  <input 
                    className="h-10 flex-1 w-full rounded-lg border-slate-200 bg-[#f6f7f8] p-3 text-sm text-slate-900 min-w-0 sm:min-w-[150px] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary" 
                    placeholder="Otros" 
                    type="text"
                    value={otherSymptom}
                    onChange={(e) => setOtherSymptom(e.target.value)}
                    disabled={isViewMode}
                  />
                </div>
              </div>
            </ClinicalChartCard>

            {/* Visual Acuity Section */}
            <ClinicalChartCard title="Agudeza Visual (Visión Lejana)" collapsible>
              <div className="overflow-x-auto">
                <table className="w-full text-center">
                  <thead className="border-b border-slate-200">
                    <tr>
                      <th className="p-3 font-medium text-slate-500 w-16">Ojo</th>
                      <th className="p-3 font-medium text-slate-500">Lejos SC</th>
                      <th className="p-3 font-medium text-slate-500">Lejos CC</th>
                      <th className="p-3 font-medium text-slate-500">PH</th>
                      <th className="p-3 font-medium text-slate-500">DIP (mm)</th>
                      <th className="p-3 font-medium text-slate-500">DNP (mm)</th>
                      <th className="p-3 font-medium text-slate-500">Altura (mm)</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="p-2 font-bold text-slate-600">OD</td>
                      <td className="p-2">
                        <input 
                          className={inputClass}
                          type="text"
                          name="od_distance_sc"
                          value={visualAcuity.od_distance_sc}
                          onChange={handleVisualAcuityChange}
                          disabled={isViewMode}
                        />
                      </td>
                      <td className="p-2">
                        <input 
                          className={inputClass}
                          type="text"
                          name="od_distance_cc"
                          value={visualAcuity.od_distance_cc}
                          onChange={handleVisualAcuityChange}
                          disabled={isViewMode}
                        />
                      </td>
                      <td className="p-2">
                        <input 
                          className={inputClass}
                          type="text"
                          name="od_ph"
                          value={visualAcuity.od_ph}
                          onChange={handleVisualAcuityChange}
                          disabled={isViewMode}
                        />
                      </td>
                      <td className="p-2" rowSpan={2}>
                        <input 
                          className={inputClass}
                          type="text"
                          name="dip_mm"
                          value={visualAcuity.dip_mm}
                          onChange={handleVisualAcuityChange}
                          disabled={isViewMode}
                        />
                      </td>
                      <td className="p-2">
                        <input 
                          className={inputClass}
                          type="text"
                          name="dnp_od_mm"
                          value={visualAcuity.dnp_od_mm}
                          onChange={handleVisualAcuityChange}
                          disabled={isViewMode}
                        />
                      </td>
                      <td className="p-2">
                        <input 
                          className={inputClass}
                          type="text"
                          name="od_height_mm"
                          value={visualAcuity.od_height_mm}
                          onChange={handleVisualAcuityChange}
                          disabled={isViewMode}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td className="p-2 font-bold text-slate-600">OI</td>
                      <td className="p-2">
                        <input 
                          className={inputClass}
                          type="text"
                          name="oi_distance_sc"
                          value={visualAcuity.oi_distance_sc}
                          onChange={handleVisualAcuityChange}
                          disabled={isViewMode}
                        />
                      </td>
                      <td className="p-2">
                        <input 
                          className={inputClass}
                          type="text"
                          name="oi_distance_cc"
                          value={visualAcuity.oi_distance_cc}
                          onChange={handleVisualAcuityChange}
                          disabled={isViewMode}
                        />
                      </td>
                      <td className="p-2">
                        <input 
                          className={inputClass}
                          type="text"
                          name="oi_ph"
                          value={visualAcuity.oi_ph}
                          onChange={handleVisualAcuityChange}
                          disabled={isViewMode}
                        />
                      </td>
                      <td className="p-2">
                        <input 
                          className={inputClass}
                          type="text"
                          name="dnp_oi_mm"
                          value={visualAcuity.dnp_oi_mm}
                          onChange={handleVisualAcuityChange}
                          disabled={isViewMode}
                        />
                      </td>
                      <td className="p-2">
                        <input 
                          className={inputClass}
                          type="text"
                          name="oi_height_mm"
                          value={visualAcuity.oi_height_mm}
                          onChange={handleVisualAcuityChange}
                          disabled={isViewMode}
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </ClinicalChartCard>

            {/* Retinoscopy Section */}
            <ClinicalChartCard title="Retinoscopía" collapsible>
              <div className="overflow-x-auto">
                <table className="w-full text-center">
                  <thead className="border-b border-slate-200">
                    <tr>
                      <th className="p-3 font-medium text-slate-500 w-16"></th>
                      <th className="p-3 font-medium text-slate-500">ESTÁTICA</th>
                      <th className="p-3 font-medium text-slate-500">AV</th>
                      <th className="p-3 font-medium text-slate-500">DINÁMICA</th>
                      <th className="p-3 font-medium text-slate-500">AV</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="p-2 font-bold text-slate-600">OD</td>
                      <td className="p-2">
                        <input 
                          className={inputClass}
                          type="text"
                          name="od_static_val"
                          value={retinoscopia.od_static_val}
                          onChange={handleRetinoscopiaChange}
                          disabled={isViewMode}
                        />
                      </td>
                      <td className="p-2">
                        <input 
                          className={inputClass}
                          type="text"
                          name="od_static_av"
                          value={retinoscopia.od_static_av}
                          onChange={handleRetinoscopiaChange}
                          disabled={isViewMode}
                        />
                      </td>
                      <td className="p-2">
                        <input 
                          className={inputClass}
                          type="text"
                          name="od_dynamic_val"
                          value={retinoscopia.od_dynamic_val}
                          onChange={handleRetinoscopiaChange}
                          disabled={isViewMode}
                        />
                      </td>
                      <td className="p-2">
                        <input 
                          className={inputClass}
                          type="text"
                          name="od_dynamic_av"
                          value={retinoscopia.od_dynamic_av}
                          onChange={handleRetinoscopiaChange}
                          disabled={isViewMode}
                        />
                      </td>
                    </tr>
                    <tr>
                      <td className="p-2 font-bold text-slate-600">OI</td>
                      <td className="p-2">
                        <input 
                          className={inputClass}
                          type="text"
                          name="oi_static_val"
                          value={retinoscopia.oi_static_val}
                          onChange={handleRetinoscopiaChange}
                          disabled={isViewMode}
                        />
                      </td>
                      <td className="p-2">
                        <input 
                          className={inputClass}
                          type="text"
                          name="oi_static_av"
                          value={retinoscopia.oi_static_av}
                          onChange={handleRetinoscopiaChange}
                          disabled={isViewMode}
                        />
                      </td>
                      <td className="p-2">
                        <input 
                          className={inputClass}
                          type="text"
                          name="oi_dynamic_val"
                          value={retinoscopia.oi_dynamic_val}
                          onChange={handleRetinoscopiaChange}
                          disabled={isViewMode}
                        />
                      </td>
                      <td className="p-2">
                        <input 
                          className={inputClass}
                          type="text"
                          name="oi_dynamic_av"
                          value={retinoscopia.oi_dynamic_av}
                          onChange={handleRetinoscopiaChange}
                          disabled={isViewMode}
                        />
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </ClinicalChartCard>

            {/* Lensometría (Gafas en Uso) - Reference Section */}
            <ClinicalChartCard title="Lensometría (Gafas en Uso)" collapsible defaultCollapsed={true}>
              <RefractionGrid
                data={lensometry}
                onChange={handleLensometryChange}
                disabled={isViewMode}
              />
            </ClinicalChartCard>

            {/* Final RX - Prescription to Print - Hero Section with Printer Icon */}
            <ClinicalChartCard title="RX Final (Receta a Imprimir)" highlight={true}>
              <RefractionGrid
                data={finalRx}
                onChange={handleFinalRxChange}
                disabled={isViewMode}
              />
            </ClinicalChartCard>

            {/* Diagnosis Section */}
            <ClinicalChartCard title="Diagnóstico Clínico / Impresión" collapsible>
              <textarea 
                className="w-full min-h-[120px] rounded-lg border border-slate-200 bg-[#f6f7f8] p-3 text-lg text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-colors resize-none"
                placeholder="Escriba sus notas aquí..."
                value={diagnosis}
                onChange={(e) => setDiagnosis(e.target.value)}
                disabled={isViewMode}
              />
            </ClinicalChartCard>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="sticky bottom-0 z-10 w-full bg-white/80 backdrop-blur-sm mt-auto">
        <div className="h-px w-full bg-slate-200"></div>
        <div className="mx-auto flex w-full max-w-6xl flex-col items-stretch gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4 sm:px-8">
          <Footer />
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
            <button
              type="button"
              onClick={handleViewHistory}
              className="w-full sm:w-auto rounded-xl bg-slate-200 px-6 py-3 text-base font-bold text-slate-600 hover:bg-slate-300 transition-colors"
            >
              Ver Historial
            </button>
            {!isViewMode && !isEditing && (
              <button
                type="button"
                onClick={handleCancel}
                className="w-full sm:w-auto rounded-xl bg-slate-200 px-6 py-3 text-base font-bold text-slate-600 hover:bg-slate-300 transition-colors"
              >
                Cancelar
              </button>
            )}
            {isEditing && (
              <button
                type="button"
                onClick={() => setIsEditing(false)}
                className="w-full sm:w-auto rounded-xl bg-slate-200 px-6 py-3 text-base font-bold text-slate-600 hover:bg-slate-300 transition-colors"
              >
                Cancelar Edición
              </button>
            )}
            {!isViewMode && (
              <PrimaryButton
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitDisabled}
                className="w-full sm:w-auto px-6 py-3"
              >
                {isEditing ? 'Guardar Cambios' : submitLabel}
              </PrimaryButton>
            )}
          </div>
        </div>
      </footer>
    </div>
  );
};
